/**
 * My Learning — the learner's own record, as a bento of glass tiles.
 *
 * The page's aurora is mixed from the subjects this learner has actually
 * touched, so their record glows their own curriculum rather than the house
 * palette. Every figure comes from the analytics spine scoped to this learner's
 * actor, so it is the same data the admin dashboards read, not a parallel tally.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Check, Loader2, PlayCircle, Clock, GraduationCap, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  LearnRoot, LearnTopBar, useLearnTheme, subjectMeta, glassPanel, glassHero, inkMuted,
} from '../components/learn/learnShared';
import { LearnAccountMenu } from '../components/learn/LearnAccountMenu';
import { useLearnAuth } from '../components/learn/LearnAuthProvider';
import { useLearnerProgress, formatWatchTime } from '../components/learn/useLearnerProgress';
import {
  fetchMyStats, fetchMyJourney,
  type LearnerStats, type JourneyLesson,
} from '../services/learnerAPI';
import { JourneyCard } from '../components/learn/JourneyCard';

export const Route = createFileRoute('/learn/me')({ component: MyLearning });

function MyLearning() {
  const { theme, toggle } = useLearnTheme();
  const { profile } = useLearnAuth();
  const { items, loading } = useLearnerProgress();
  const [stats, setStats] = useState<LearnerStats | null>(null);
  const [journey, setJourney] = useState<JourneyLesson[] | null>(null);

  useEffect(() => {
    fetchMyStats().then(setStats).catch(() => setStats(null));
    // The journey supersedes the flat lesson list: it carries every watch and
    // every check attempt, grouped under the lesson they belong to.
    fetchMyJourney().then(setJourney).catch(() => setJourney([]));
  }, []);

  const touched = items.filter((i) => i.status !== 'not_started');
  const firstName = profile?.displayName?.split(' ')[0];

  // The learner's own subjects light the page.
  const mySubjects = [...new Set(touched.map((i) => i.subject).filter(Boolean))] as string[];

  // Per-subject standing, from the same rows — no second query, no disagreement.
  const bySubject = mySubjects
    .map((subject) => {
      const rows = touched.filter((i) => i.subject === subject);
      return {
        subject,
        done: rows.filter((r) => r.status === 'completed').length,
        total: rows.length,
      };
    })
    .sort((a, b) => b.done - a.done || b.total - a.total);

  return (
    <LearnRoot theme={theme} subjects={mySubjects}>
      <LearnTopBar theme={theme} onToggle={toggle} right={<LearnAccountMenu />} />

      <main className="learn-shell-narrow mx-auto px-6 pb-24 pt-10">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Your record</p>
        <h1 className="mt-1 font-serif text-[40px] leading-[1.14] tracking-tight">
          {firstName ? `${firstName}'s learning` : 'My learning'}
        </h1>

        {loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-[#5B2EE5]" />
          </div>
        ) : touched.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Kpi icon={<Check className="h-4 w-4" />} label="Completed" value={String(stats?.videosCompleted ?? 0)} accent="#0E8A80" />
              <Kpi icon={<PlayCircle className="h-4 w-4" />} label="In progress" value={String(stats?.videosInProgress ?? 0)} accent="#5B2EE5" />
              <Kpi icon={<Clock className="h-4 w-4" />} label="Time learning" value={stats ? formatWatchTime(stats.watchTimeMs) : '—'} />
              <Kpi
                icon={<GraduationCap className="h-4 w-4" />}
                label="Checks taken"
                value={String(stats?.quizzesTaken ?? 0)}
                sub={stats?.avgFirstAttemptScore != null ? `${Math.round(stats.avgFirstAttemptScore)}% average` : undefined}
              />
            </div>

            {bySubject.length > 0 && (
              <section className={cn(glassPanel, 'mt-4 p-5')}>
                <h2 className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>By subject</h2>
                <div className="mt-4 flex flex-col gap-3">
                  {bySubject.map(({ subject, done, total }) => {
                    const meta = subjectMeta(subject);
                    return (
                      <div key={subject} className="flex items-center gap-3">
                        <span className="w-[124px] shrink-0 truncate text-[13px] font-semibold" style={{ color: meta.color }}>
                          {subject}
                        </span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-[#191831]/[0.08] dark:bg-white/10">
                          <span
                            className="block h-full rounded-full"
                            style={{ width: `${total ? Math.max(4, (done / total) * 100) : 4}%`, background: meta.color }}
                          />
                        </span>
                        <span className={cn('w-[62px] shrink-0 text-right text-[12.5px] font-semibold tabular-nums', inkMuted)}>
                          {done} of {total}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="mt-10">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Lesson by lesson</p>
                  <h2 className="mt-1 font-serif text-[28px] leading-[1.14] tracking-tight">Your journey</h2>
                </div>
                <p className={cn('max-w-[40ch] text-[13px]', inkMuted)}>
                  Each lesson with every time you watched it and every check you took on it. Open one to see the dates.
                </p>
              </div>

              {journey === null ? (
                <div className={cn(glassPanel, 'mt-4 grid place-items-center py-14')}>
                  <Loader2 className="h-5 w-5 animate-spin text-[#5B2EE5]" />
                </div>
              ) : journey.length === 0 ? (
                <div className={cn(glassPanel, 'mt-4 px-6 py-10 text-center')}>
                  <p className={cn('text-[14px]', inkMuted)}>Nothing recorded yet — watch a lesson and it appears here.</p>
                </div>
              ) : (
                <div className="mt-4 flex flex-col gap-3">
                  {journey.map((l) => <JourneyCard key={l.shareToken} lesson={l} />)}
                </div>
              )}
            </section>

          </>
        )}
      </main>
    </LearnRoot>
  );
}

function Kpi({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className={cn(glassPanel, 'p-4')}>
      <div
        className={cn('flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em]', !accent && inkMuted)}
        style={accent ? { color: accent } : undefined}
      >
        {icon}
        {label}
      </div>
      <p className="mt-2.5 font-serif text-[32px] leading-[0.9] tracking-tight tabular-nums">{value}</p>
      {sub && <p className={cn('mt-1.5 text-[12.5px]', inkMuted)}>{sub}</p>}
    </div>
  );
}

function EmptyState() {
  return (
    <div className={cn(glassHero, 'mt-8 px-8 py-14 text-center')}>
      <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-[#5B2EE5]/10">
        <Sparkles className="h-6 w-6 text-[#5B2EE5] dark:text-[#BFA8FA]" />
      </span>
      <h2 className="font-serif text-[28px] leading-[1.28] tracking-tight">Nothing here yet</h2>
      <p className={cn('mx-auto mt-2 max-w-sm text-[15px] leading-relaxed', inkMuted)}>
        Start any lesson and your progress, scores, and time spent will show up here.
      </p>
      <Link
        to="/learn"
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#5B2EE5] px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(91,46,229,0.9)] transition-transform hover:scale-[1.03]"
      >
        Browse lessons <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
